/** Adapted from standalone-search f83fe2d1 via selected Registrar V2. See docs/delivery/iteration2/fusion-shared-kit/TRANSFER.json. */
import * as React from "react";
import { cn } from "@/lib/utils";

export type DetailCardSeverity = "normal" | "info" | "warn" | "danger";

const SEVERITY_SURFACE: Record<DetailCardSeverity, string> = {
  normal: "",
  info: "bg-status-info-surface",
  warn: "bg-status-warning-surface",
  danger: "bg-destructive-surface",
};

export interface DetailCardProps extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
  as?: "section" | "div";
  ref?: React.Ref<HTMLElement>;
  /** Head region — typically <DetailCardHead>. */
  head?: React.ReactNode;
  /** Right-side affordance rendered as a sibling of the head, outside head buttons. */
  headAffix?: React.ReactNode;
  /** Hero metric — typically <HeroMetric> or <ConfidenceRing>. */
  hero?: React.ReactNode;
  /** Body content — the collapsed preview if the card has an expand region. */
  body?: React.ReactNode;
  /** Expanded content — rendered below the body with grid-row animation. */
  expanded?: React.ReactNode;
  /** Stable ID for the expanded region. */
  expandedId?: string;
  /** Whether expanded content is currently visible. No-op if expanded is absent. */
  isOpen?: boolean;
  /** Complete semantic result surface, never an edge marker. */
  severity?: DetailCardSeverity;
  /** Spans the full grid width. */
  fullWidth?: boolean;
  /**
   * Child already provides its own card chrome. DetailCard becomes a transparent
   * grid cell so the scaffold can land before inner components migrate.
   */
  transparent?: boolean;
  className?: string;
  /** Raw children for layouts that do not fit the head/hero/body composition. */
  children?: React.ReactNode;
}

export function DetailCard({
  head,
  headAffix,
  hero,
  body,
  expanded,
  expandedId,
  isOpen = false,
  severity = "normal",
  fullWidth = false,
  transparent = false,
  className,
  children,
  as = "div",
  ref,
  ...props
}: DetailCardProps) {
  const Component = as as "section";
  if (transparent) {
    return (
      <Component ref={ref} {...props} className={cn(fullWidth && "col-span-full", className)}>
        {children ?? (
          <>
            {head}
            {hero}
            {body}
          </>
        )}
      </Component>
    );
  }

  return (
    <Component
      ref={ref}
      {...props}
      data-severity={severity}
      data-expanded={isOpen ? "true" : undefined}
      className={cn(
        "flex flex-col gap-2.5 overflow-hidden rounded-lg border border-border bg-card px-4 py-3.5",
        "shadow-[var(--elevation-card)] transition-[box-shadow,border-color,transform] duration-[var(--motion-card)] ease-[var(--ease-card)]",
        "hover:shadow-[var(--elevation-card-hover)] hover:border-[var(--border-strong)]",
        isOpen && "border-[var(--border-strong)] shadow-[var(--elevation-card-expanded)]",
        SEVERITY_SURFACE[severity],
        fullWidth && "col-span-full",
        className,
      )}
    >
      {children ?? (
        <>
          {headAffix && head ? (
            <div className="flex items-center gap-1.5">
              <div className="min-w-0 flex-1">{head}</div>
              <div className="shrink-0">{headAffix}</div>
            </div>
          ) : (
            head
          )}
          {hero}
          {body}
          {expanded && (
            <div
              id={expandedId}
              aria-hidden={!isOpen}
              inert={!isOpen}
              className={cn(
                "grid transition-[grid-template-rows,margin] duration-[var(--motion-card-slow)] ease-[var(--ease-card)] motion-reduce:transition-none",
                isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                !isOpen && "-mt-2.5",
              )}
            >
              <div className="min-h-0 overflow-hidden">
                <div className="border-t border-rule pt-2">{expanded}</div>
              </div>
            </div>
          )}
        </>
      )}
    </Component>
  );
}
